import _defineProperty from "/Users/lenkazivkovic/Documents/gatsby_websites/Woocommerce/tcp/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { warning } from 'hey-listen';
import { secondsToMilliseconds } from '../utils/time-conversion.mjs';
import { instantAnimationState } from '../utils/use-instant-transition-state.mjs';
import { createAcceleratedAnimation } from './waapi/create-accelerated-animation.mjs';
import { createInstantAnimation } from './create-instant-animation.mjs';
import { animate } from './legacy-popmotion/index.mjs';
import { inertia } from './legacy-popmotion/inertia.mjs';
import { getDefaultTransition } from './utils/default-transitions.mjs';
import { isAnimatable } from './utils/is-animatable.mjs';
import { getKeyframes } from './utils/keyframes.mjs';
import { getValueTransition, isTransitionDefined } from './utils/transitions.mjs';
import { supports } from './waapi/supports.mjs';

/**
 * A list of values that can be hardware-accelerated.
 */
var acceleratedValues = new Set(["opacity"]);
var createMotionValueAnimation = function createMotionValueAnimation(valueName, value, target) {
  var transition = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return function (_onComplete) {
    var valueTransition = getValueTransition(transition, valueName) || {};
    /**
     * Most transition values are currently completely overwritten by value-specific
     * transitions. In the future it'd be nicer to blend these transitions. But for now
     * delay actually does inherit from the root transition if not value-specific.
     */
    var delay = valueTransition.delay || transition.delay || 0;
    /**
     * Elapsed isn't a public transition option but can be passed through from
     * optimized appear effects in milliseconds.
     */
    var _transition$elapsed = transition.elapsed,
      elapsed = _transition$elapsed === void 0 ? 0 : _transition$elapsed;
    elapsed = elapsed - secondsToMilliseconds(delay);
    var keyframes = getKeyframes(value, valueName, target, valueTransition);
    /**
     * Check if we're able to animate between the start and end keyframes,
     * and throw a warning if we're attempting to animate between one that's
     * animatable and another that isn't.
     */
    var originKeyframe = keyframes[0];
    var targetKeyframe = keyframes[keyframes.length - 1];
    var isOriginAnimatable = isAnimatable(valueName, originKeyframe);
    var isTargetAnimatable = isAnimatable(valueName, targetKeyframe);
    warning(isOriginAnimatable === isTargetAnimatable, "You are trying to animate ".concat(valueName, " from \"").concat(originKeyframe, "\" to \"").concat(targetKeyframe, "\". ").concat(originKeyframe, " is not an animatable value - to enable this animation set ").concat(originKeyframe, " to a value animatable to ").concat(targetKeyframe, " via the `style` property."));
    var options = _objectSpread(_objectSpread({
      keyframes: keyframes,
      velocity: value.getVelocity()
    }, valueTransition), {}, {
      elapsed: elapsed,
      onUpdate: function onUpdate(v) {
        value.set(v);
        valueTransition.onUpdate && valueTransition.onUpdate(v);
      },
      onComplete: function onComplete() {
        _onComplete();
        valueTransition.onComplete && valueTransition.onComplete();
      }
    });
    if (!isOriginAnimatable || !isTargetAnimatable || instantAnimationState.current || valueTransition.type === false) {
      /**
       * If we can't animate this value, or the global instant animation flag is set,
       * or this is simply defined as an instant transition, return an instant transition.
       */
      return createInstantAnimation(options);
    } else if (valueTransition.type === "inertia") {
      /**
       * If this is an inertia animation, we currently don't support pre-generating
       * keyframes for this as such it must always run on the main thread.
       */
      var animation = inertia(options);
      return function () {
        return animation.stop();
      };
    }
    /**
     * If there's no transition defined for this value, we can generate
     * unqiue transition settings for this value.
     */
    if (!isTransitionDefined(valueTransition)) {
      options = _objectSpread(_objectSpread({}, options), getDefaultTransition(valueName, options));
    }
    /**
     * Both WAAPI and our internal animation functions use durations
     * as defined by milliseconds, while our external API defines them
     * as seconds.
     */
    if (options.duration) {
      options.duration = secondsToMilliseconds(options.duration);
    }
    if (options.repeatDelay) {
      options.repeatDelay = secondsToMilliseconds(options.repeatDelay);
    }
    var visualElement = value.owner;
    var element = visualElement && visualElement.current;
    var canAccelerateAnimation = supports.waapi() && acceleratedValues.has(valueName) && !options.repeatDelay && options.repeatType !== "mirror" && options.damping !== 0 && visualElement && element instanceof HTMLElement && !visualElement.getProps().onUpdate;
    if (canAccelerateAnimation) {
      /**
       * If this animation is capable of being run via WAAPI, then do so.
       */
      return createAcceleratedAnimation(value, valueName, options);
    } else {
      /**
       * Otherwise, fall back to the main thread.
       */
      var _animation = animate(options);
      return function () {
        return _animation.stop();
      };
    }
  };
};
export { createMotionValueAnimation };